<template>
  <div class="view-container">
    <b-form @submit.prevent="onSubmit" class="card p-5 mb-5">
      <div v-if="isEditMode" class="alert alert-info py-2 px-3 mb-4 d-flex align-items-center" style="font-size: 0.9rem; border-radius: 8px;">
        <i class="icon-info mr-2"></i>
        <span>You are currently editing <strong>{{ form.name || 'this dashboard' }}</strong>. Updating these details will reflect immediately across all connected views.</span>
      </div>

      <h3 class="text-center">{{ isEditMode ? 'Edit Dashboard' : 'Customize MSDAT' }}</h3>
      <small class="text-center d-block mb-5">
        {{ isEditMode ? 'Update the general settings for your dashboard.' : 'Configure your MSDAT instance with basic settings.' }}
      </small>

      <!-- Basic Information -->
      <h5 class="mb-3">Basic Information</h5>
      <b-form-group id="input-group-1" label="Dashboard Name" label-for="input-1">
        <b-form-input
          id="input-1"
          v-model="form.name"
          placeholder="e.g., MSDAT Nigeria"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group id="input-group-2" label="Dashboard Title" label-for="input-2">
        <b-form-input
          id="input-2"
          v-model="form.title"
          placeholder="e.g., Nigeria Health Dashboard"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group id="input-group-3" label="Description" label-for="input-3">
        <b-form-textarea
          id="input-3"
          v-model="form.description"
          placeholder="Enter a description for your dashboard"
          rows="3"
          required
        ></b-form-textarea>
      </b-form-group>

      <div class="d-flex justify-content-between mt-4">
        <b-button v-if="isEditMode" type="button" size="lg" variant="outline-primary" @click="saveAndExit" :disabled="isSaving">
          <i class="fas fa-save mr-2"></i>
          {{ isSaving ? 'Saving...' : 'Save Changes & Exit' }}
        </b-button>
        <b-button type="submit" size="lg" variant="primary" :class="{'ml-auto': !isEditMode}">
          <span>Proceed to Data Upload</span>
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { defaultConfig } from '../../utils/configSchema';

export default {
  data() {
    return {
      form: { ...defaultConfig },
      isSaving: false,
    };
  },
  computed: {
    ...mapState('configurer', ['dashboardConfig', 'activeInstanceId']),
    isEditMode() {
      return !!this.activeInstanceId;
    },
  },
  mounted() {
    // If edit mode or revisiting step, populate form
    if (this.dashboardConfig) {
      this.form = {
        ...this.form,
        ...this.dashboardConfig,
      };
    }
  },
  methods: {
    ...mapActions('configurer', ['createInstance']),
    async saveAndExit() {
      this.isSaving = true;
      try {
        // Commit whatever is currently in the form
        this.$store.commit('configurer/SET_DASHBOARD_CONFIG', this.form);

        // Push update to Firebase
        await this.createInstance();

        this.$swal({
          icon: 'success',
          title: 'Dashboard Updated!',
          text: 'Your changes have been saved successfully.',
        });

        // Redirect back to profile
        this.$router.push('/configure/user/profile');
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update dashboard. Please try again.',
        });
      } finally {
        this.isSaving = false;
      }
    },
    onSubmit() {
      // Save configuration to Vuex store
      this.$store.commit('configurer/SET_DASHBOARD_CONFIG', this.form);

      this.$router.push('/configure/user/data');
    },
  },
};
</script>

<style scoped>
.view-container {
  display: flex;
  justify-content: center;
  padding: 0 10px;
  min-width: max-content;
}

form.card {
  width: 600px;
  /* Glassmorphism */
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  border-radius: 20px;
}

/* Override Bootstrap Form Control */
::v-deep .custom-file-label,
::v-deep .form-control,
::v-deep .custom-select {
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    height: auto;
    padding: 10px 15px;
    box-shadow: none;
    font-size: 0.95rem;
}

::v-deep .form-control:focus,
::v-deep .custom-select:focus,
::v-deep .custom-file-input:focus ~ .custom-file-label {
    background: #ffffff;
    border-color: #007D53;
    box-shadow: 0 0 0 3px rgba(0, 125, 83, 0.1);
}

::v-deep .custom-file-label::after {
    background: rgba(0, 125, 83, 0.1);
    color: #007D53;
    border-left: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0 12px 12px 0;
}

form :is(small, label) {
  color: #57606f;
  font-size: 0.95rem;
  font-weight: 500;
}

h3 {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

h5 {
  color: #007D53;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

::v-deep .btn-primary {
  background-color: #007D53;
  border-color: #007D53;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  transition: all 0.3s ease;
}

::v-deep .btn-primary:hover {
  background-color: #006644;
  border-color: #006644;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 125, 83, 0.3);
}
</style>
